import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react';

import { DeviceUnknown, Lock, Paid } from '@mui/icons-material';
import {
  AvatarGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';

import { SMALL_ROWS_PER_PAGE_OPTIONS } from '@/constants/table';
import { Posts } from '@/models/posts';
import { Violations } from '@/models/violations';
import { getRestrictionParts } from '@/utils/creator';
import { getPrettyDateWithTime } from '@/utils/datesAndTime/datesAndTime';
import getRoute from '@/utils/getRoute';
import CreateCreator from '@/views/creators/create';

import Avatar from '../avatar';
import Button from '../button';
import Dialog from '../dialog';
import InstagramIcon from '../icons/InstagramIcon';
import TikTokIcon from '../icons/TikTokIcon';
import YoutubeIcon from '../icons/YoutubeIcon';

type Platform = {
  type:
    | 'Instagram Post'
    | 'Instagram Reel'
    | 'Instagram Story'
    | 'Youtube Short'
    | 'Youtube Video'
    | 'Youtube Livestream'
    | 'TikTok Video'
    | 'TikTok Story'
    | 'Unknown';
  icon: React.JSX.Element;
};

const TIKTOK_DISCLOSURE_TOOLTIP =
  "This shows whether the creator has used TikTok's platform features to disclose advertising: promotional content indicates self-promotion, while paid partnership signifies advertising on behalf of a third party. How this disclosure is shown depends on whether you view the post in a browser or in the TikTok app. It might also depend on your location and other factors that we have not yet identified.";

const isTikTokPlatform = (platform: Posts.Details['platform']): boolean =>
  platform.startsWith('tiktok');

const disclosureMapper = (
  disclosureType: Posts.PlatformDisclosureType,
  platform: Posts.Details['platform']
) => {
  switch (disclosureType) {
    case 'paidPartnership':
      return isTikTokPlatform(platform) ? 'Paid Partnership' : 'Sponsored';
    case 'promotionalContent':
      return 'Promotional Content';
    case 'paidPromotion':
      return 'Paid Promotion';
    default:
      return '';
  }
};

const platformMapper = (platform: Posts.Details['platform']): Platform => {
  switch (platform) {
    case 'instagram-story':
      return {
        type: 'Instagram Story',
        icon: <InstagramIcon fontSize="large" />
      };
    case 'instagram-post':
      return {
        type: 'Instagram Post',
        icon: <InstagramIcon fontSize="large" />
      };
    case 'instagram-reel':
      return {
        type: 'Instagram Reel',
        icon: <InstagramIcon fontSize="large" />
      };
    case 'youtube-video':
      return { type: 'Youtube Video', icon: <YoutubeIcon fontSize="large" /> };
    case 'youtube-short':
      return { type: 'Youtube Short', icon: <YoutubeIcon fontSize="large" /> };
    case 'youtube-livestream':
      return {
        type: 'Youtube Livestream',
        icon: <YoutubeIcon fontSize="large" />
      };
    case 'tiktok-post':
      return { type: 'TikTok Video', icon: <TikTokIcon fontSize="large" /> };
    case 'tiktok-story':
      return { type: 'TikTok Story', icon: <TikTokIcon fontSize="large" /> };
    default:
      return { type: 'Unknown', icon: <DeviceUnknown fontSize="large" /> };
  }
};

const PostInfo = ({ data }: { data: Posts.Details | Violations.Details }) => {
  const disclosureText = data.platformDisclosureType ? (
    <Typography variant="body2" noWrap>
      {disclosureMapper(data.platformDisclosureType, data.platform)}
    </Typography>
  ) : null;

  return (
    <Stack
      style={{
        maxWidth: '50%',
        cursor: data.url ? 'pointer' : 'default'
      }}
      direction="row"
      alignItems="center"
      gap={1}
      onClick={() => data.url && window.open(data.url, '_blank')}
    >
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.5}
        >
          {data.restrictions.length > 0 && (
            <Tooltip
              slotProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
              arrow
              title={
                <Stack gap={1}>
                  {data.restrictions.map((restriction, i) => {
                    const { title, detail } = getRestrictionParts(restriction);
                    return (
                      <Stack key={i}>
                        <Typography variant="body2" fontWeight="bold">
                          {title}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {detail}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              }
            >
              <Lock color="action" sx={{ height: 20 }} />
            </Tooltip>
          )}
          <Typography sx={{ fontWeight: 'bold', textAlign: 'right' }} noWrap>
            {platformMapper(data.platform).type}
          </Typography>
        </Stack>
        {disclosureText && (
          <Stack direction="row">
            <Paid color="action" sx={{ height: 20 }} />
            <Tooltip
              arrow
              title={
                isTikTokPlatform(data.platform) ? TIKTOK_DISCLOSURE_TOOLTIP : ''
              }
            >
              {disclosureText}
            </Tooltip>
          </Stack>
        )}
        {!data?.platformDisclosureType && data?.platformDisclosure && (
          <Stack direction="row">
            <Paid color="action" sx={{ height: 20 }} />
            <Typography variant="body2" noWrap>
              sponsored
            </Typography>
          </Stack>
        )}
      </Stack>
      {platformMapper(data.platform).icon}
    </Stack>
  );
};

const getCollaboratorText = (
  creator: Posts.CreatorItem | Violations.Creator,
  collaborators: Array<Posts.Collaborator>
) => {
  if (collaborators.length > 1) {
    return `${creator.name}, ${collaborators[0].name} and ${
      collaborators.length - 1
    } Creators`;
  }
  if (collaborators.length === 1) {
    return `${creator.name} and ${collaborators[0].name}`;
  }
  return creator.name;
};

const CreatorInfo = ({
  data,
  navigate
}: {
  data: Posts.Details | Violations.Details;
  navigate: any;
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isOpenCreateCreatorDialog, setIsOpenCreateCreatorDialog] =
    useState(false);
  const [isCollabsDialogOpen, setCollabsDialogOpen] = useState(false);
  const [creatorToAdd, setCreatorToAdd] = useState({
    name: '',
    accounts: {
      youtube: [],
      instagram: [],
      tiktok: []
    }
  });
  const [addedCreators, setAddedCreators] = useState<string[]>([]);
  const creatorGroups = data.creator.groups || [];
  const collaborators = data.collaborators || [];

  const rows = useMemo(() => [data.creator, ...collaborators], [data]);

  const handleChangePage = (
    _: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const getCreatorToAdd = useCallback(
    (row: Posts.Collaborator | Violations.Creator) => {
      const { platform } = data;
      const platf = platform.split('-')[0];
      const accountToAdd = platf === 'instagram' ? row.name : `@${row.name}`;

      setCreatorToAdd((prev) => ({
        ...prev,
        name: row.name,
        accounts: {
          ...prev.accounts,
          [platf]: [accountToAdd]
        }
      }));
    },
    [data]
  );

  const isCreatorAdded = useCallback(
    (creator: Posts.Collaborator | Violations.Creator) =>
      Boolean(creator.id) || addedCreators.includes(creator.name),
    [addedCreators]
  );

  const handleCreatorAdded = useCallback((creatorName: string) => {
    setAddedCreators((prev) => [...prev, creatorName]);
  }, []);

  return (
    <>
      <Stack
        style={{
          cursor:
            data.creator.id || collaborators.length ? 'pointer' : 'default'
        }}
        direction="row"
        alignItems="center"
        gap={1}
      >
        <AvatarGroup
          max={3}
          color="primary"
          total={collaborators.length + 1}
          renderSurplus={(surplus) => (
            <Typography>+{surplus.toString()[0]}</Typography>
          )}
          onClick={
            collaborators.length >= 1
              ? () => setCollabsDialogOpen(true)
              : undefined
          }
          sx={{
            '& .MuiAvatarGroup-avatar': {
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          <Tooltip
            arrow
            placement="top"
            title={creatorGroups.length ? creatorGroups.join(', ') : ''}
            disableHoverListener={!creatorGroups.length}
          >
            <span style={{ backgroundColor: 'transparent' }}>
              <Avatar
                src={data.creator.avatarUrl || ''}
                name={data.creator.name}
                width="40px"
                onClick={(e) =>
                  data.creator.id &&
                  collaborators.length === 0 &&
                  navigate(getRoute.creators.DETAIL(data.creator.id), e)
                }
              />
            </span>
          </Tooltip>
          {collaborators.map((collaborator) => {
            const groups = collaborator.groups || [];
            return (
              <Tooltip
                arrow
                key={collaborator.id}
                placement="top"
                title={groups.length ? groups.join(', ') : ''}
                disableHoverListener={!groups.length}
              >
                <span
                  style={{ backgroundColor: 'transparent', marginLeft: -8 }}
                >
                  <Avatar
                    src={collaborator.avatarUrl || ''}
                    name={collaborator.name}
                    width="40px"
                  />
                </span>
              </Tooltip>
            );
          })}
        </AvatarGroup>
        <Stack style={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              textOverflow: 'ellipsis',
              overflow: 'hidden'
            }}
            onClick={
              collaborators.length >= 1
                ? () => setCollabsDialogOpen(true)
                : undefined
            }
          >
            {getCollaboratorText(data.creator, collaborators)}
          </Typography>
          <Typography variant="body2" noWrap>
            {'createTime' in data
              ? getPrettyDateWithTime(data.createTime)
              : getPrettyDateWithTime(data.uploadDate)}
          </Typography>
        </Stack>
      </Stack>
      <Dialog
        maxWidth="sm"
        fullWidth
        onClose={() => setCollabsDialogOpen(false)}
        open={isCollabsDialogOpen}
        dialogContent={
          <TableContainer component={Stack}>
            <Table>
              <TableBody>
                {paginatedRows.map((row) => (
                  <TableRow
                    hover
                    key={row.name}
                    sx={{
                      cursor: row.id ? 'pointer' : 'default'
                    }}
                    onClick={(e) =>
                      row.id && navigate(getRoute.creators.DETAIL(row.id), e)
                    }
                  >
                    <TableCell width={50} align="left">
                      <Avatar
                        src={row.avatarUrl || ''}
                        name={row.name}
                        width="40px"
                      />
                    </TableCell>
                    <TableCell align="left">{row.name}</TableCell>
                    <TableCell align="right">
                      <Button
                        disabled={isCreatorAdded(row)}
                        text="Add creator"
                        onClick={() => {
                          setIsOpenCreateCreatorDialog(true);
                          getCreatorToAdd(row);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={SMALL_ROWS_PER_PAGE_OPTIONS}
            />
          </TableContainer>
        }
        title="Creators"
        actions={
          <Button onClick={() => setCollabsDialogOpen(false)} text="close" />
        }
      />
      {isOpenCreateCreatorDialog && (
        <CreateCreator
          creatorToAdd={creatorToAdd}
          isOpen={isOpenCreateCreatorDialog}
          onClose={() => {
            setIsOpenCreateCreatorDialog(false);
          }}
          onCreatorAdded={handleCreatorAdded}
        />
      )}
    </>
  );
};

interface CreatorDetailsProps {
  dataTestId?: string;
  id?: string;
  data: Posts.Details | Violations.Details;
  navigate?: any;
}

const CreatorDetails = ({
  dataTestId = 'result-card',
  id,
  data,
  navigate
}: CreatorDetailsProps) => {
  const hasExistingCollabs = useMemo(
    () => data.collaborators?.some((collab) => collab.id !== null),
    [data.collaborators]
  );

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      data-testid={dataTestId}
      id={id}
    >
      {data.creator.id || hasExistingCollabs ? (
        <CreatorInfo data={data} navigate={navigate} />
      ) : (
        <Tooltip title="This creator has been deleted" arrow>
          <Stack>
            <CreatorInfo data={data} navigate={navigate} />
          </Stack>
        </Tooltip>
      )}
      {data.url ? (
        <PostInfo data={data} />
      ) : (
        <Tooltip title="Link unavailable" arrow>
          <Stack>
            <PostInfo data={data} />
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
};

export default CreatorDetails;
